/* eslint-disable no-restricted-globals */
/* eslint-disable no-param-reassign */
import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Map as ImmutableMap } from "immutable";
import { IFilterOptionDef } from "@ag-grid-community/core";
import { ICellRendererParams } from "ag-grid-community";
import { LicenseManager } from "@ag-grid-enterprise/core";
import "./styles/Leaderboard.scss";
import {
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Collapse,
  Grid,
  Box,
} from "@material-ui/core";
import {
  ExpandLess,
  ExpandMore,
  TextFormat,
  GetApp,
  UnfoldLess,
  UnfoldMore,
  Add,
  Fullscreen,
  FilterList,
} from "@material-ui/icons";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import type { TDocumentDefinitions, Margins } from "pdfmake/interfaces";
import pdfMake from "pdfmake/build/pdfmake";
import { Delta, Historical } from "./QueueLeaderboard";
import RcButton from "./UiUtilities/Button";
import UserHeadlines from "./headlines/UserHeadlines";
import { CXAgent } from "./CxDashboard";

function durationFormatter(params: { value: number | undefined }) {
  if (params?.value === undefined) return "00:00";
  return `${Math.floor(params.value / 60 / 1000)
    .toString()
    .padStart(2, "0")}:${Math.floor(
    (params.value - Math.floor(params.value / 60 / 1000) * 60 * 1000) / 1000
  )
    .toString()
    .padStart(2, "0")}`;
}

LicenseManager.setLicenseKey(
  "CompanyName=Resource Software International Ltd,LicensedApplication=Shadow,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=1,AssetReference=AG-032296,SupportServicesEnd=20_October_2023_[v2]_MTY5Nzc1NjQwMDAwMA==0c008d8dba7b9997038f1e1fee335d4a"
);

// Custom Wrapper
const ClickableCellWrapper = ({
  children, 
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  className: string; 
}) => (
  <div
    className={`${className}`}
    onClick={onClick}
    onKeyDown={onClick}
    role="button"
    tabIndex={0}
    style={{ cursor: "pointer" }}
  >
    {children}
  </div>
)

const textFilterParams = {
  filterOptions: [
    {
      displayKey: "textGreaterThen",
      displayName: "Greater Than",
      test: (filterVal: any, cellVal: any) => {
        // minutes:seconds (00:00) format
        if (/^(\d{1,}:\d{2})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0");
          return cellVal > formattedFilterValue;
        }
        // minutes:seconds (00:0) format
        if (/^(\d{1,}:\d{1})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0").concat("0");
          return cellVal > formattedFilterValue;
        }
        // minutes: (00:) format
        if (/^(\d{1,}:)$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat("00");
          return cellVal > formattedFilterValue;
        }
        // minutes format
        if (/^\d{1,}$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat(":00");
          return cellVal > formattedFilterValue;
        }
        return false;
      },
    },
    {
      displayKey: "textLessThen",
      displayName: "Less Than",
      test: (filterVal: any, cellVal: any) => {
        // minutes:seconds (00:00) format
        if (/^(\d{1,}:\d{2})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0");
          return cellVal < formattedFilterValue;
        }
        // minutes:seconds (00:0) format
        if (/^(\d{1,}:\d{1})$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(5, "0").concat("0");
          return cellVal < formattedFilterValue;
        }
        // minutes: (00:) format
        if (/^(\d{1,}:)$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat("00");
          return cellVal < formattedFilterValue;
        }
        // minutes format
        if (/^\d{1,}$/.test(filterVal)) {
          const formattedFilterValue = filterVal.padStart(2, "0").concat(":00");
          return cellVal < formattedFilterValue;
        }
        return false;
      },
    },
    "equals",
    "contains",
    "notContains",
  ] as IFilterOptionDef[],
};

const cxStateFilterParams = {
  filterOptions: [
    "contains",
    "notContains",
    "equals",
    "notEqual",
    "startsWith",
    "endsWith",
    {
      displayKey: "blank",
      displayName: "Blank",
      test: (filterValue: any, cellValue: any) => {
        if (cellValue === undefined || cellValue === "" || cellValue === null) {
          return true;
        }
        return false;
      },
      hideFilterInput: true,
    },
    {
      displayKey: "notBlank",
      displayName: "Not Blank",
      test: (filterValue: any, cellValue: any) => {
        if (cellValue === undefined || cellValue === "" || cellValue === null) {
          return false;
        }
        return true;
      },
      hideFilterInput: true,
    },
  ] as IFilterOptionDef[],
};

function getDocDefinition(agGridApi: any, agGridColumnApi: any) {
  const PDF_HEADER_HEIGHT = 25;
  const PDF_ROW_HEIGHT = 15;
  const PDF_HEADER_COLOR = "#f8f8f8";
  const PDF_INNER_BORDER_COLOR = "#dde2eb";
  const PDF_OUTER_BORDER_COLOR = "#babfc7";
  const PDF_ODD_BKG_COLOR = "#ffffff";
  const PDF_EVEN_BKG_COLOR = "#ffffff";

  return (() => {
    const columnsToExport = getColumnsToExport();

    const widths = getExportedColumnsWidths(columnsToExport);

    const rowsToExport = getRowsToExport(columnsToExport);

    const body = [columnsToExport, ...rowsToExport];

    // const footer: DynamicContent = function (currentPage: number, pageCount: number) {
    //   return {
    //     text: `${currentPage.toString()} of ${pageCount}`,
    //     margin: [20],
    //   };
    // };

    const pageMargins: Margins = [10, 20, 10, 40];

    const heights = (rowIndex: number) =>
      rowIndex < 1 ? PDF_HEADER_HEIGHT : PDF_ROW_HEIGHT;

    const fillColor = (rowIndex: number, node: any) => {
      if (rowIndex < node.table.headerRows) {
        return PDF_HEADER_COLOR;
      }
      return rowIndex % 2 === 0 ? PDF_ODD_BKG_COLOR : PDF_EVEN_BKG_COLOR;
    };

    const hLineWidth = (i: number, node: any) =>
      i === 0 || i === node.table.body.length ? 1 : 1;

    const vLineWidth = (i: number, node: any) =>
      i === 0 || i === node.table.widths.length ? 1 : 0;

    const hLineColor = (i: number, node: any) =>
      i === 0 || i === node.table.body.length
        ? PDF_OUTER_BORDER_COLOR
        : PDF_INNER_BORDER_COLOR;

    const vLineColor = (i: number, node: any) =>
      i === 0 || i === node.table.widths.length
        ? PDF_OUTER_BORDER_COLOR
        : PDF_INNER_BORDER_COLOR;

    const docDefintiion: TDocumentDefinitions = {
      pageOrientation: "landscape",
      // footer,
      content: [
        {
          style: "myTable",
          table: {
            headerRows: 1,
            widths,
            body,
            heights,
          },
          layout: {
            fillColor,
            hLineWidth,
            vLineWidth,
            hLineColor,
            vLineColor,
          },
        },
      ],
      styles: {
        myTable: {
          margin: [0, 0, 0, 0],
        },
        tableHeader: {
          fontSize: 8,
          bold: true,
          margin: [0, PDF_HEADER_HEIGHT / 3, 0, 0],
        },
        tableCell: {
          fontSize: 8,
          // margin: [0, 15, 0, 0]
        },
      },
      pageMargins,
    };

    return docDefintiion;
  })();

  function getColumnsToExport() {
    const columnsToExport: any = [];

    agGridColumnApi.getAllGridColumns().forEach((col: any) => {
      const headerCell = createHeaderCell(col);
      if (col.visible) {
        columnsToExport.push(headerCell);
      }
    });

    return columnsToExport;
  }

  function getRowsToExport(columnsToExport: any) {
    const rowsToExport: any = [];

    agGridApi.forEachNodeAfterFilterAndSort((node: any) => {
      const rowToExport = columnsToExport.map((col: any) => {
        const cellValue = agGridApi.getValue(col.colId, node);
        const tableCell = createTableCell(cellValue, col.colId);
        return tableCell;
      });
      rowsToExport.push(rowToExport);
    });

    return rowsToExport;
  }

  function getExportedColumnsWidths(columnsToExport: any) {
    return columnsToExport.map(() => `${100 / columnsToExport.length}%`);
  }

  function createHeaderCell(col: any) {
    const headerCell: any = {};

    // eslint-disable-next-line no-prototype-builtins
    const isColGroup = col.hasOwnProperty("children");

    if (isColGroup) {
      headerCell.text = col.originalColumnGroup.colGroupDef.headerName;
      headerCell.colSpan = col.children.length;
      headerCell.colId = col.groupId;
    } else {
      let { headerName } = col.colDef;

      if (col.sort) {
        headerName += ` (${col.sort})`;
      }
      if (col.filterActive) {
        headerName += ` [FILTERING]`;
      }

      headerCell.text = headerName;
      headerCell.colId = col.getColId();
    }

    headerCell.style = "tableHeader";

    return headerCell;
  }

  function createTableCell(cellValue: any, colId: any) {
    const tableCell = {
      text: cellValue !== undefined ? cellValue : "",
      style: "tableCell",
    };

    return tableCell;
  }
}

const NewLeaderboard = React.memo(
  ({
    setUserLeaderboardApi,
    smsData,
    leaderboardDeltas,
    historicalUsers,
    mode,
    tab,
    navPage,
    hasRingCx,
    widgetData,
    setExcludedUsers,
    setExcludedDepartments,
    setExcluded,
    setExcludedFacilities,
    setExcludedLocations,
    blocked,
    rcId,
    fullscreen,
    setFullscreen,
    headlinesAnchorEl,
    setHeadlinesAnchorEl,
    sidebar,
    historicals,
    displayHeadlines,
    setDisplayHeadlines,
    orgId,
    cxAgents,
    adminClick,
    isAdmin,
  }: {
    setUserLeaderboardApi: any;
    smsData: any; // Please type
    widgetData: any;
    leaderboardDeltas: Delta[];
    historicalUsers: ImmutableMap<string, any>;
    mode: string;
    tab: string;
    navPage: string;
    hasRingCx: boolean;
    setExcludedUsers: React.Dispatch<
      React.SetStateAction<Set<string> | undefined>
    >;
    setExcluded: React.Dispatch<React.SetStateAction<Set<string> | undefined>>;
    fullscreen: undefined | string;
    setFullscreen: React.Dispatch<SetStateAction<undefined | string>>;
    setExcludedDepartments: React.Dispatch<
      React.SetStateAction<Set<string> | undefined>
    >;
    setExcludedFacilities: React.Dispatch<React.SetStateAction<Set<string>>>;
    setExcludedLocations: React.Dispatch<
      React.SetStateAction<Set<string> | undefined>
    >;
    blocked: Set<string> | undefined;
    rcId: string;
    headlinesAnchorEl: any;
    setHeadlinesAnchorEl: any;
    sidebar: boolean;
    historicals: Historical[];
    displayHeadlines: boolean;
    setDisplayHeadlines: React.Dispatch<SetStateAction<boolean>>;
    orgId: string;
    cxAgents: CXAgent[] | undefined;
    adminClick: (userId: string) => void;
    isAdmin: boolean;
  }) => {
    const gridRef = useRef<AgGridReact>(null);
    const [gridReady, setGridReady] = useState(false);
    const [totalLine, setTotalLine] = useState<Delta[]>([]);
    const [theme, setTheme] = useState("ag-theme-alpine");
    const [verticalPercent, setVerticalPercent] = useState<number>(50);
    const [expandLeaderboard, setExpandLeaderboard] = useState(true);
    const [anchorExport, setAnchorExport] = useState<null | HTMLElement>(null);
    const [fontSize, setFontSize] = useState<string>("small");
    const [defaultColState, setDefaultColState] = useState<any>(null);
    const [clearColumn, setClearColumn] = useState<boolean | undefined>(
      undefined
    );
    const [clearFilters, setClearFilters] = useState<boolean | undefined>(
      undefined
    );

    useEffect(() => {
      setTheme(mode === "light" ? "ag-theme-alpine" : "ag-theme-alpine-dark");
    }, [mode]);

    const [smsUserSent, setSmsUserSent] = useState([]);
    const [smsUserReceived, setSmsUserReceived] = useState([]);
    const [smsUserTotal, setSmsUserTotal] = useState([]);

    useEffect(() => {
      if (gridReady) {
        setUserLeaderboardApi(gridRef.current);
      }
    }, [gridReady]);
    // useEffect(() => {
    //   if (smsData) {
    //     console.log('smsData:', JSON.stringify(smsData, null, 2));
    //   }
    // }, [smsData]);

    // useEffect(() => {
    //   if (smsUserSent) {
    //     console.log('smsUserSent:', JSON.stringify(smsUserSent, null, 2));
    //   }
    // }, [smsUserSent]);

    // useEffect(() => {
    //   if (smsUserReceived) {
    //     console.log('smsUserReceived:', JSON.stringify(smsUserReceived, null, 2));
    //   }
    // }, [smsUserReceived]);

    // useEffect(() => {
    //   if (smsUserTotal) {
    //     console.log('smsUserTotal:', JSON.stringify(smsUserTotal, null, 2));
    //   }
    // }, [smsUserTotal]);

    // Init row data
    const [rowData] = useState<any[]>(() =>
      historicalUsers
        .valueSeq()
        .map((user) => ({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          department: user.department,
          extensionNumber: user.extensionNumber,
          presence: (() => {
            switch ((user.display || "").split(":")[0]) {
              case "DND":
                return "Do not disturb";
              case "AVL":
                return "Available";
              case "BSY":
                return "Busy";
              case "INV":
                return "Invisible";
              default:
                return user.display;
            }
          })(),
          status: (user.display || "").substring(4) || user.lastPresnceStatus,
          cxStatus: user.cxStatus,
          maxDuration: 0,
          maxRingTimeInbound: 0,
          maxRingTimeOutbound: 0,
          maxRingTimeInternal: 0,
          maxTalkTimeInbound: 0,
          maxTalkTimeOutbound: 0,
          maxTalkTimeInternal: 0,
          maxHoldTimeInbound: 0,
          maxHoldTimeOutbound: 0,
          maxHoldTimeInternal: 0,
          duration: 0,
          ringTimeInbound: 0,
          ringTimeOutbound: 0,
          ringTimeInternal: 0,
          talkTimeInbound: 0,
          talkTimeOutbound: 0,
          talkTimeInternal: 0,
          holdTimeInbound: 0,
          holdTimeOutbound: 0,
          holdTimeInternal: 0,
          vmGreetingTime: 0,
          faxSendTime: 0,
          vmDurationTime: 0,
          count: 0,
          ringCountInbound: 0,
          ringCountOutbound: 0,
          ringCountInternal: 0,
          talkCountInbound: 0,
          talkCountOutbound: 0,
          talkCountInternal: 0,
          holdCountInbound: 0,
          holdCountOutbound: 0,
          holdCountInternal: 0,
          faxCount: 0,
          vmAbandonedCount: 0,
          vmLeftCount: 0,
          answeredTime: 0,
          abandonedTime: 0,
          missedTime: 0,
          voicemailTime: 0,
          answeredCount: 0,
          abandonedCount: 0,
          missedCount: 0,
          voicemailCount: 0,
          vmCount: 0,
          rcVideoCount: 0,
          hangupCount: 0,
          handled: 0,
          transferredToVm: 0,
          smsUserSent: 0,
          smsUserReceived: 0,
          smsUserTotal: 0,
          late: user.late === true,
        }))
        .toArray()
    );

    const openExport = Boolean(anchorExport);
    
    // Renderers
    const customCellRenderer = useCallback(
      (params: ICellRendererParams<any>) => {

        if (params !== undefined || params !== null) {
          // Id for Profile popup

          let handleClick = () => {};
          if(isAdmin) {
            handleClick = () => adminClick(params.data.id)
          }

          
          switch (params.value) {
            case "Available":
              return (
                <ClickableCellWrapper className={`cell-wrapper ${params.data.late ? 'bg-blue' : 'bg-green'}`} onClick={handleClick}>
                  <div className="cell available-cell" />
                  {params.value}
                </ClickableCellWrapper>
              );
            case "Busy":
              return (
                <ClickableCellWrapper className={`cell-wrapper ${params.data.late ? 'bg-blue' : 'bg-busy'}`} onClick={handleClick}>
                  <div className="cell busy-cell" />
                  {params.value}
                </ClickableCellWrapper>
              );
            case "Do not disturb":
              return (
                <ClickableCellWrapper className={`cell-wrapper ${params.data.late ? 'bg-blue' : 'bg-dnd'}`} onClick={handleClick}>
                  <div className="cell dnd-cell" />
                  {params.value}
                </ClickableCellWrapper>
              );
            case "Invisible":
              return (
                <ClickableCellWrapper className={`cell-wrapper ${params.data.late ? 'bg-blue' : 'bg-inv'}`} onClick={handleClick}>
                  <div className="cell inv-cell" />
                  {params.value}
                </ClickableCellWrapper>
              );

            default:
              return <div className="cell-wrapper">{params.value}</div>;
          }
        }
        return <div className="cell-wrapper">Undefined</div>;
      },
      []
    );

    const customExtRenderer = useCallback(
      (params: ICellRendererParams<any>) => {
        // Id for Profile popup
        let handleClick = () => {};
        if(isAdmin) {
          handleClick = () => adminClick(params.data.id)
        }

        const { presence, extensionNumber, late } = params.data;
        
        switch (presence) {
          case "Available":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-green'}`} onClick={handleClick}>
                <div className="cell available-cell" />
                {extensionNumber}
              </ClickableCellWrapper>
            );
          case "Busy":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-busy'}`} onClick={handleClick}>
                <div className="cell busy-cell" />
                {extensionNumber}
              </ClickableCellWrapper>
            );
          case "Do not disturb":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-dnd'}`} onClick={handleClick}>
                <div className="cell dnd-cell" />
                {extensionNumber}
              </ClickableCellWrapper>
            );
          case "Invisible":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-inv'}`} onClick={handleClick}>
                <div className="cell inv-cell" />
                {extensionNumber}
              </ClickableCellWrapper>
            );
          default:
            return <div className="cell-wrapper"> {extensionNumber}</div>;
        }
      },
      []
    );
    const customAuxRenderer = useCallback(
      (params: ICellRendererParams<any>) => {
        // Id for Profile popup
        let handleClick = () => {};
        if(isAdmin) {
          handleClick = () => adminClick(params.data.id)
        }

        // Use params.value instead of params.data for status/late
        const { status, late, presence } = params.data;

        switch (presence) {
          case "Available":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-green'}`} onClick={handleClick}>
                <div className="cell available-cell" />
                {status}
              </ClickableCellWrapper>
            );
          case "Busy":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-busy'}`} onClick={handleClick}>
                <div className="cell busy-cell" />
                {status}
              </ClickableCellWrapper>
            );
          case "Do not disturb":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-dnd'}`} onClick={handleClick}>
                <div className="cell dnd-cell" />
                {status}
              </ClickableCellWrapper>
            );
          case "Invisible":
            return (
              <ClickableCellWrapper className={`cell-wrapper ${late ? 'bg-blue' : 'bg-inv'}`} onClick={handleClick}>
                <div className="cell inv-cell" />
                {status}
              </ClickableCellWrapper>
            );
          default:
            return <div className="cell-wrapper"> {status}</div>;
        }
      },
      []
    );

    // Custom Cell Renderer for User ID
    // const customUserIdRenderer = useCallback(
    //   (params: ICellRendererParams<any>) => {
    //     if (params !== undefined || params !== null) {
    //       // Id for Profile popup
    //       const handleClick = () => adminClick(params.data.id)

    //       switch(true) {
    //         case params?.data?.id !== undefined:
    //           return (
    //             <ClickableCellWrapper className="" onClick={handleClick}>
    //               {params.data.id}
    //             </ClickableCellWrapper>
    //           ); 
    //         default:
    //           return <div className=""> </div>;
    //       }
    //     }
    //     return <div className="">Undefined</div>;
    //   },
    //   []
    // );

    useEffect(() => {
      if (gridRef?.current && gridReady && cxAgents && hasRingCx) {
        const updatedRows: any[] = [];

        gridRef.current.api.forEachNodeAfterFilter((node) => {
          const getCxProperties = cxAgents.find(
            (agent) => agent.rcUserId === node.data.id
          );
          const updated = {
            ...node.data,
            cxStatus: getCxProperties?.agentState || "UNKNOWN",
          };

          updatedRows.push(updated);
        });

        if (updatedRows.length) {
          gridRef.current.api.applyTransaction({ update: updatedRows });

          gridRef.current.api.refreshCells({ force: true });
        }
      }
    }, [gridReady, cxAgents, hasRingCx]);

    const customCxPresenceRenderer = useCallback(
      (params: ICellRendererParams<any>) => {
        // Id for Profile popup
        let handleClick = () => {};
        if(isAdmin) {
          handleClick = () => adminClick(params.data.id)
        }
        const { cxStatus } = params.data;
        switch (cxStatus) {
          case "AVAILABLE":
            return (
              <ClickableCellWrapper className="cell-wrapper bg-green" onClick={handleClick}>
                <div className="cell available-cell" />
                {cxStatus}
              </ClickableCellWrapper>
            );
          case "TRANSITION":
          case "BREAK-AFTER-CALL":
          case "ENGAGED":
          case "PENDING-DISPOSITION":
            return (
              <ClickableCellWrapper className="cell-wrapper bg-busy" onClick={handleClick}>
                <div className="cell busy-cell" />
                {cxStatus}
              </ClickableCellWrapper>
            );
          case "ON-BREAK":
          case "AWAY":
          case "LUNCH":
          case "TRAINING":
          case "WORKING":
          case "RNA-STATE":
            return (
              <ClickableCellWrapper className="cell-wrapper bg-dnd" onClick={handleClick}>
                <div className="cell dnd-cell" />
                {cxStatus}
              </ClickableCellWrapper>
            );
          case "OFF-LINE":
          case "unknown":
          case "UNKNOWN":
            return (
              <div className="cell-wrapper bg-inv">
                <div className="cell inv-cell" />
                {cxStatus}
              </div>
            );
          default:
            return <div className="cell-wrapper"> {cxStatus}</div>;
        }
      },
      []
    );

    const getRowId = useMemo(
      () => (params: { data: { id: string } }) => params.data.id,
      []
    );

    // ColumnDefs
    const colDefs = useMemo(() => {
      const defs: any[] = [
        {
          field: "firstName",
          headerName: "First name",
          cellStyle: { textAlign: "left" },
          //  
          cellClass: "notranslate",
        },
        {
          field: "lastName",
          headerName: "Last name",
          //  
          cellClass: "notranslate",
        },
        {
          field: "department",
          headerName: "Department",
          //  
          cellClass: "notranslate",
        },
        {
          field: "extensionNumber",
          headerName: "EXT",
          pinned: "left",
          filter: "agSetColumnFilter",
          width: 100,
          //  
          cellClass: "notranslate",
          cellRenderer: customExtRenderer,
          valueGetter: (params: any) => {
            return params.data.extensionNumber;
          },
          valueFormatter: (params: any) => {
            return {
              extensionNumber: params.data.extensionNumber,
              presence: params.data.presence
            }
          }
          // comparator: (valueA: any, valueB: any) => {
          //   const a = Number(valueA.extensionNumber);
          //   const b = Number(valueB.extensionNumber);
          //   return a - b;
          // },
        },
        {
          field: "id",
          headerName: "User ID",
          hide: true,
          // cellRenderer: customUserIdRenderer,
          filter: "agSetColumnFilter",
          cellClass: "notranslate",
        },
        {
          field: "presence",
          headerName: hasRingCx ? "EX Presence" : "Presence",
          hide: false,
          cellRenderer: customCellRenderer,
          filter: "agSetColumnFilter",
          //  
        },
        {
          field: "status",
          headerName: "Custom Presence",
          cellRenderer: customAuxRenderer,
          filter: "agSetColumnFilter",
          cellClass: "notranslate",
          // Add valueGetter to include both status, late, and presence
          valueGetter: (params: any) => {
            return params.data.status;
          },
          valueFormatter: (params: any) => {
            return {
              status: params.data.status,
              late: params.data.late,
              presence: params.data.presence,
            };
          },
        },
      ];
      if (hasRingCx) {
        defs.push({
          field: "cxStatus",
          headerName: "CX Presence",
          cellRenderer: customCxPresenceRenderer,
          // filterParams: cxStateFilterParams,
          filter: "agSetColumnFilter",
          // valueGetter: (params: any) => params.data.agentState,
           
        });
      }
      defs.push(
        ...[
          {
            field: "count",
            headerName: "Total Calls",
            filter: "agNumberColumnFilter",
            //  
            cellClass: "notranslate",
          },
          {
            field: "duration",
            headerName: "Total Duration",
            filter: "agNumberColumnFilter",
            //  
            cellRenderer: durationFormatter,
          },
          {
            field: "durationAvg",
            headerName: "Average Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
          },
          {
            field: "maxDuration",
            headerName: "Longest Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
          },
          {
            field: "ringCountInbound",
            headerName: "Inbound Offered",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "ringTimeInbound",
            headerName: "Inbound Ring Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
          },
          {
            field: "ringTimeAvgInbound",
            headerName: "Average Inbound Ring Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
          },
          {
            field: "maxRingTimeInbound",
            headerName: "Longest Inbound Ring Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
          },
          {
            field: "ringCountOutbound",
            headerName: "Outbound Offered",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "maxRingTimeOutbound",
            headerName: "Outbound Ring Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
          },
          {
            field: "ringTimeAvgOutbound",
            headerName: "Average Outbound Ring Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
          },
          {
            field: "maxRingTimeOutbound",
            headerName: "Longest Outbound Ring Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "talkCountInbound",
            headerName: "Inbound Answered",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "talkTimeInbound",
            headerName: "Inbound Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "talkTimeAvgInbound",
            headerName: "Average Inbound Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "maxTalkTimeInbound",
            headerName: "Longest Inbound Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "talkCountOutbound",
            headerName: "Outbound Answered",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "talkTimeOutbound",
            headerName: "Outbound Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "talkTimeAvgOutbound",
            headerName: "Average Outbound Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "maxTalkTimeOutbound",
            headerName: "Longest Outbound Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "holdCountInbound",
            headerName: "Inbound Hold Count",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "holdTimeInbound",
            headerName: "Inbound Hold Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "holdTimeAvgInbound",
            headerName: "Average Inbound Hold Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "maxHoldTimeInbound",
            headerName: "Longest Inbound Hold Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "holdCountOutbound",
            headerName: "Outbound Hold Count",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "holdTimeOutbound",
            headerName: "Outbound Hold Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "holdTimeAvgOutbound",
            headerName: "Average Outbound Hold Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "maxHoldTimeOutbound",
            headerName: "Longest Outbound Hold Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "faxCount",
            headerName: "Inbound Fax",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "faxSendTime",
            headerName: "Inbound Fax Duration",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "rcVideoCount",
            headerName: "Transfer to RCV",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "vmCount",
            headerName: "Calls to VM",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "vmAbandonedCount",
            headerName: "Abandoned VM",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "vmLeftCount",
            headerName: "Received VM",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "vmGreetingTime",
            headerName: "Total VM Greeting Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "vmDurationTime",
            headerName: "Total VM Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "hangupCount",
            headerName: "Near End Hangups",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "ringCountInternal",
            headerName: "Internal Offered",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "talkCountInternal",
            headerName: "Internal Answered Count",
            filter: "agNumberColumnFilter",
            // headerClass: "notranslate"
          },
          {
            field: "talkTimeInternal",
            headerName: "Internal Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "maxRingTimeInternal",
            headerName: "Longest Internal Ring Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          {
            field: "maxTalkTimeInternal",
            headerName: "Longest Internal Answered Duration",
            filter: "agNumberColumnFilter",
            cellRenderer: durationFormatter,
            // headerClass: "notranslate"
          },
          // {
          //   field: "smsUserSent",
          //   headerName: "SMS Sent",
          //   filter: "agNumberColumnFilter",
          // },
          // {
          //   field: "smsUserReceived",
          //   headerName: "SMS Received",
          //   filter: "agNumberColumnFilter",
          // },
          // {
          //   field: "smsUserTotal",
          //   headerName: "SMS Total",
          //   filter: "agNumberColumnFilter",
          // }
        ]
      );
      return defs;
    }, []);

    const defaultColDef = useMemo(
      () => ({
        sortable: true,
        resizable: true,
        filter: true,
        cellClass: " notarnslate ",
        cellClassRules: {
          small: () => fontSize === "small",
          medium: () => fontSize === "medium",
          large: () => fontSize === "large",
        },
      }),
      [fontSize]
    );
    // Update rowData with sms values using applyTransaction
    // useEffect(() => {
    //   if (!gridRef.current?.api || smsData.length === 0 || !gridReady) return; // Needed to prevent race condition
    //   if (smsData && smsData.users) {
    //     smsData.users.forEach((smsUser: any) => {
    //       const updatedUser = {
    //         // Needs to include current node also
    //         smsUserSent: smsUser.Outbound,
    //         smsUserReceived: smsUser.Inbound,
    //         smsUserTotal: smsUser.Total,
    //       };

    //         gridRef.current.api.applyTransaction({
    //           update: [updatedUser], // Apply the updated values for the specific user
    //         });
    //     });
    //   }
    // }, [smsData, gridReady]);
    useEffect(() => {
      if (!gridRef.current?.api || leaderboardDeltas.length === 0 || !gridReady)
        return;
      // Initialize a new Delta object with default values
      const initializeUser = (): Delta => ({
        userId: "",
        duration: 0,
        ringTimeInbound: 0,
        ringTimeOutbound: 0,
        ringTimeInternal: 0,
        talkTimeInbound: 0,
        talkTimeOutbound: 0,
        talkTimeInternal: 0,
        holdTimeInbound: 0,
        holdTimeOutbound: 0,
        holdTimeInternal: 0,
        vmGreetingTime: 0,
        faxSendTime: 0,
        vmDurationTime: 0,
        count: 0,
        ringCountInbound: 0,
        ringCountOutbound: 0,
        ringCountInternal: 0,
        talkCountInbound: 0,
        talkCountOutbound: 0,
        talkCountInternal: 0,
        holdCountInbound: 0,
        holdCountOutbound: 0,
        holdCountInternal: 0,
        faxCount: 0,
        vmAbandonedCount: 0,
        vmLeftCount: 0,
        answeredTime: 0,
        abandonedTime: 0,
        missedTime: 0,
        voicemailTime: 0,
        answeredCount: 0,
        abandonedCount: 0,
        missedCount: 0,
        voicemailCount: 0,
        vmCount: 0,
        rcVideoCount: 0,
        hangupCount: 0,
        handled: 0,
        transferredToVm: 0,
        maxDuration: 0,
        maxRingTimeInbound: 0,
        maxRingTimeOutbound: 0,
        maxRingTimeInternal: 0,
        maxTalkTimeInbound: 0,
        maxTalkTimeOutbound: 0,
        maxTalkTimeInternal: 0,
        maxHoldTimeInbound: 0,
        maxHoldTimeOutbound: 0,
        maxHoldTimeInternal: 0,
        firstCall: "",
        lastCall: "",
      });

      const maxFields = [
        "maxDuration",
        "maxRingTimeInbound",
        "maxRingTimeOutbound",
        "maxRingTimeInternal",
        "maxTalkTimeInbound",
        "maxTalkTimeOutbound",
        "maxTalkTimeInternal",
        "maxHoldTimeInbound",
        "maxHoldTimeOutbound",
        "maxHoldTimeInternal",
      ];
      // Merge deltas into a single object per user
      const mergedDeltas = leaderboardDeltas.reduce<Record<string, Delta>>(
        (acc, delta) => {
          const { userId } = delta;

          if (!acc[userId]) {
            acc[userId] = initializeUser();
            acc[userId].userId = userId;
          }

          const user = acc[userId];

          // Aggregate numerical fields
          Object.keys(delta).forEach((key) => {
            if (
              typeof delta[key as keyof Delta] === "number" &&
              !maxFields.includes(key)
            ) {
              user[key as keyof Delta] =
                (user[key as keyof Delta] || 0) +
                (delta[key as keyof Delta] as number);
            }
          });

          // Update max fields
          maxFields.forEach((field) => {
            user[field as keyof Delta] = Math.max(
              user[field as keyof Delta] || 0,
              delta[field as keyof Delta] || 0
            );
          });

          // Update firstCall and lastCall timestamps
          if (
            delta.firstCall &&
            (!user.firstCall || delta.firstCall < user.firstCall)
          ) {
            user.firstCall = delta.firstCall;
          }
          if (
            delta.lastCall &&
            (!user.lastCall || delta.lastCall > user.lastCall)
          ) {
            user.lastCall = delta.lastCall;
          }

          return acc;
        },
        {}
      );
      // Update rows in the grid
      const updatedRows = Object.values(mergedDeltas)
        .map((delta) => {
          const existingRow = gridRef.current?.api.getRowNode(
            delta.userId
          )?.data;
          if (!existingRow) {
            return null;
          }

          const updatedRow = { ...existingRow };

          // Utility functions
          const sumField = (field: keyof Delta) =>
            (existingRow[field] || 0) + (delta[field] || 0);

          const maxField = (field: keyof Delta) =>
            Math.max(existingRow[field] || 0, delta[field] || 0);

          // Update fields using utility functions
          updatedRow.count = sumField("count");
          updatedRow.duration = sumField("duration");
          updatedRow.maxDuration = maxField("maxDuration");
          updatedRow.totalAvg = updatedRow.totalSum / updatedRow.count;

          // Update specific categories (e.g., inbound, outbound, etc.)
          const categories = [
            {
              suffix: "Inbound",
              prefixes: [
                "maxRingTime",
                "maxTalkTime",
                "maxHoldTime",
                "ringCount",
                "talkCount",
                "holdCount",
                "ringTime",
                "talkTime",
                "holdTime",
              ],
            },
            {
              suffix: "Outbound",
              prefixes: [
                "maxRingTime",
                "maxTalkTime",
                "maxHoldTime",
                "ringCount",
                "talkCount",
                "holdCount",
                "ringTime",
                "talkTime",
                "holdTime",
              ],
            },
            {
              suffix: "Internal",
              prefixes: [
                "maxRingTime",
                "maxTalkTime",
                "maxHoldTime",
                "ringCount",
                "talkCount",
                "holdCount",
                "ringTime",
                "talkTime",
                "holdTime",
              ],
            },
            {
              suffix: "Time",
              prefixes: ["answered", "abandoned", "missed", "voicemail"],
            },
            {
              suffix: "Count",
              prefixes: [
                "answered",
                "abandoned",
                "missed",
                "voicemail",
                "rcVideo",
                "hangUp",
              ],
            },
          ];

          categories.forEach(({ suffix, prefixes }) => {
            prefixes.forEach((prefix) => {
              const key = `${prefix}${suffix}` as keyof Delta;
              if (prefix.includes("max")) {
                updatedRow[key] = maxField(key);
              } else {
                updatedRow[key] = sumField(key);
              }
            });
          });

          const prefixSuffixCats = [
            {
              prefix: "vm",
              suffixes: [
                "AbandonedCount",
                "LeftCount",
                "GreetingTime",
                "DurationTime",
              ],
            },
            { prefix: "fax", suffixes: ["Count", "SendTime"] },
          ];

          prefixSuffixCats.forEach((cat) => {
            cat.suffixes.forEach((suffix) => {
              const key = `${cat.prefix}${suffix}`;
              updatedRow[key] = sumField(key);
            });
          });

          // Additional fields
          updatedRow.handled = sumField("handled");
          updatedRow.transferredToVm = sumField("transferredToVm");

          // Averages
          updatedRow.durationAvg = updatedRow.duration / updatedRow.count || 0;
          updatedRow.ringTimeAvgInbound =
            updatedRow.ringTimeInbound / updatedRow.ringCountInbound || 0;
          updatedRow.ringTimeAvgOutbound =
            updatedRow.ringTimeOutbound / updatedRow.ringCountOutbound || 0;

          updatedRow.talkTimeAvgInbound =
            updatedRow.talkTimeInbound / updatedRow.talkCountInbound || 0;
          updatedRow.talkTimeAvgOutbound =
            updatedRow.talkTimeOutbound / updatedRow.talkCountOutbound || 0;

          updatedRow.holdTimeAvgInbound =
            updatedRow.holdTimeInbound / updatedRow.holdCountInbound || 0;
          updatedRow.holdTimeAvgOutbound =
            updatedRow.holdTimeOutbound / updatedRow.holdCountOutbound || 0;

          return updatedRow;
        })
        .filter(Boolean);

      if (updatedRows.length > 0) {
        if (updatedRows.length > 0) {
          gridRef.current.api.applyTransaction({ update: updatedRows });

          gridRef.current.api.refreshCells({ force: true });

          // Get filtered user IDs
          const filteredUserIds = new Set<string>();
          gridRef.current.api.forEachNodeAfterFilter((node) => {
            if (node.data && node.data.id) {
              filteredUserIds.add(node.data.id);
            }
          });

          // Filter deltas based on visible user IDs
          const filteredDeltas = leaderboardDeltas.filter((delta) =>
            filteredUserIds.has(delta.userId)
          );

          const pinnedBottomRowData = totalLine[0] || initializeUser();
          filteredDeltas.forEach((delta) => {
            // Apply each delta to the pinnedBottomRowData
            Object.keys(delta).forEach((key) => {
              if (typeof delta[key] === "number" && !key.endsWith("Avg")) {
                if (key.startsWith("max")) {
                  // For max fields, take the maximum value
                  pinnedBottomRowData[key] = Math.max(
                    pinnedBottomRowData[key] || 0,
                    delta[key]
                  );
                } else {
                  // For other numerical fields, add the delta to the existing total
                  pinnedBottomRowData[key] =
                    (pinnedBottomRowData[key] || 0) + delta[key];
                }
              }
            });
          });

          // Recalculate averages using updated totals and counts
          const calculateAverage = (total: number, count: number): number =>
            count > 0 ? total / count : 0;

          pinnedBottomRowData.durationAvg = calculateAverage(
            pinnedBottomRowData.duration || 0,
            pinnedBottomRowData.count || 1
          );
          pinnedBottomRowData.ringTimeAvgInbound = calculateAverage(
            pinnedBottomRowData.ringTimeInbound || 0,
            pinnedBottomRowData.ringCountInbound || 1
          );
          pinnedBottomRowData.ringTimeAvgOutbound = calculateAverage(
            pinnedBottomRowData.ringTimeOutbound || 0,
            pinnedBottomRowData.ringCountOutbound || 1
          );
          pinnedBottomRowData.talkTimeAvgInbound = calculateAverage(
            pinnedBottomRowData.talkTimeInbound || 0,
            pinnedBottomRowData.talkCountInbound || 1
          );
          pinnedBottomRowData.talkTimeAvgOutbound = calculateAverage(
            pinnedBottomRowData.talkTimeOutbound || 0,
            pinnedBottomRowData.talkCountOutbound || 1
          );
          pinnedBottomRowData.holdTimeAvgInbound = calculateAverage(
            pinnedBottomRowData.holdTimeInbound || 0,
            pinnedBottomRowData.holdCountInbound || 1
          );
          pinnedBottomRowData.holdTimeAvgOutbound = calculateAverage(
            pinnedBottomRowData.holdTimeOutbound || 0,
            pinnedBottomRowData.holdCountOutbound || 1
          );

          // Update the pinned bottom row data
          setTotalLine([pinnedBottomRowData]);
        }
      }
    }, [leaderboardDeltas, gridReady]);

    useEffect(() => {
      if (gridRef.current && gridRef.current.api && gridReady) {
        const updates: any[] = [];
        gridRef.current.api.forEachNode((node) => {
          const user = historicalUsers.get(node.data.id);
          const status =
            (user?.display || "").substring(4) || user.lastPresnceStatus;
          if (
            user &&
            (user.lastPresenceStatus !== node.data.presence ||
              status !== node.data.status ||
              user.late !== node.data.late)
          ) {
            // Merge updated fields with existing row data
            updates.push({
              ...node.data, // Existing row data
              presence: (() => {
                switch ((user.display || "").split(":")[0]) {
                  case "DND":
                    return "Do not disturb";
                  case "AVL":
                    return "Available";
                  case "BSY":
                    return "Busy";
                  case "INV":
                    return "Invisible";
                  default:
                    return user.display;
                }
              })(),
              // cxStatus: "AVAILABLE",
              status,
              late: user.late === true,
            });
          }
        });

        if (updates.length > 0) {
          gridRef.current.api.applyTransaction({ update: updates });

          gridRef.current.api.refreshCells({ force: true });
        }
      }
    }, [historicalUsers, gridReady]);

    const onFilterChanged = useCallback(() => {
      if (!gridRef.current?.api) return;
      localStorage.setItem(
        "userModel",
        JSON.stringify(gridRef.current.api.getFilterModel())
      );
      // Initialize a new Delta object with default values
      const initializeUser = (): Delta => ({
        userId: "",
        duration: 0,
        ringTimeInbound: 0,
        ringTimeOutbound: 0,
        ringTimeInternal: 0,
        talkTimeInbound: 0,
        talkTimeOutbound: 0,
        talkTimeInternal: 0,
        holdTimeInbound: 0,
        holdTimeOutbound: 0,
        holdTimeInternal: 0,
        vmGreetingTime: 0,
        faxSendTime: 0,
        vmDurationTime: 0,
        count: 0,
        ringCountInbound: 0,
        ringCountOutbound: 0,
        ringCountInternal: 0,
        talkCountInbound: 0,
        talkCountOutbound: 0,
        talkCountInternal: 0,
        holdCountInbound: 0,
        holdCountOutbound: 0,
        holdCountInternal: 0,
        faxCount: 0,
        vmAbandonedCount: 0,
        vmLeftCount: 0,
        answeredTime: 0,
        abandonedTime: 0,
        missedTime: 0,
        voicemailTime: 0,
        answeredCount: 0,
        abandonedCount: 0,
        missedCount: 0,
        voicemailCount: 0,
        vmCount: 0,
        rcVideoCount: 0,
        hangupCount: 0,
        handled: 0,
        transferredToVm: 0,
        maxDuration: 0,
        maxRingTimeInbound: 0,
        maxRingTimeOutbound: 0,
        maxRingTimeInternal: 0,
        maxTalkTimeInbound: 0,
        maxTalkTimeOutbound: 0,
        maxTalkTimeInternal: 0,
        maxHoldTimeInbound: 0,
        maxHoldTimeOutbound: 0,
        maxHoldTimeInternal: 0,
        firstCall: "",
        lastCall: "",
        smsUserSent: 0,
        smsUserReceived: 0,
        smsUserTotal: 0,
      });
      if (totalLine[0]) {
        // Initialize the pinned bottom row data
        const pinnedBottomRowData = initializeUser();

        // Accumulate totals for all visible rows
        gridRef.current.api.forEachNodeAfterFilter((node) => {
          if (node.data) {
            const totalData = node.data;

            Object.keys(totalData).forEach((field) => {
              if (
                typeof totalData[field] === "number" &&
                !field.endsWith("Avg")
              ) {
                if (field.startsWith("max")) {
                  // For max fields, take the maximum value
                  pinnedBottomRowData[field] = Math.max(
                    pinnedBottomRowData[field] || 0,
                    totalData[field]
                  );
                } else {
                  // For other numerical fields, add the value to the total
                  pinnedBottomRowData[field] =
                    (pinnedBottomRowData[field] || 0) + totalData[field];
                }
              }
            });
          }
        });

        // Recalculate averages using accumulated totals and counts
        const calculateAverage = (total: number, count: number): number =>
          count > 0 ? total / count : 0;

        pinnedBottomRowData.durationAvg = calculateAverage(
          pinnedBottomRowData.duration || 0,
          pinnedBottomRowData.count || 1
        );
        pinnedBottomRowData.ringTimeAvgInbound = calculateAverage(
          pinnedBottomRowData.ringTimeInbound || 0,
          pinnedBottomRowData.ringCountInbound || 1
        );
        pinnedBottomRowData.ringTimeAvgOutbound = calculateAverage(
          pinnedBottomRowData.ringTimeOutbound || 0,
          pinnedBottomRowData.ringCountOutbound || 1
        );
        pinnedBottomRowData.talkTimeAvgInbound = calculateAverage(
          pinnedBottomRowData.talkTimeInbound || 0,
          pinnedBottomRowData.talkCountInbound || 1
        );
        pinnedBottomRowData.talkTimeAvgOutbound = calculateAverage(
          pinnedBottomRowData.talkTimeOutbound || 0,
          pinnedBottomRowData.talkCountOutbound || 1
        );
        pinnedBottomRowData.holdTimeAvgInbound = calculateAverage(
          pinnedBottomRowData.holdTimeInbound || 0,
          pinnedBottomRowData.holdCountInbound || 1
        );
        pinnedBottomRowData.holdTimeAvgOutbound = calculateAverage(
          pinnedBottomRowData.holdTimeOutbound || 0,
          pinnedBottomRowData.holdCountOutbound || 1
        );

        // Update the pinned bottom row data
        setTotalLine([pinnedBottomRowData]);
      }
      if (Object.keys(gridRef?.current?.api.getFilterModel()).length > 0) {
        if (
          Array.from(Object.keys(gridRef.current.api.getFilterModel()))
            .length === 1 &&
          Array.from(Object.keys(gridRef.current.api.getFilterModel()))[0] ===
            "id"
        ) {
          setClearFilters(false);
        } else {
          setClearFilters(true);
        }
      }
      if (Object.keys(gridRef?.current?.api.getFilterModel()).length <= 0) {
        setClearFilters(false);
      }
    }, [totalLine, clearFilters]);

    const applySetFilter = useCallback(() => {
      if (gridRef.current && gridRef.current.api && gridReady) {
        const filterInstance = gridRef.current.api.getFilterInstance("id");
        if (filterInstance) {
          filterInstance.setModel({
            values: widgetData.map((data: { id: any }) => data.id),
          });
          gridRef.current.api.onFilterChanged();
        }
      }
    }, [widgetData, gridReady, clearFilters]);

    useEffect(() => {
      applySetFilter();
    }, [widgetData, gridReady, clearFilters]);

    const smaller = useCallback(() => {
      setVerticalPercent((prev) => (prev < 20 ? prev : prev - 10));
    }, []);

    const larger = useCallback(() => {
      setVerticalPercent((prev) => (prev > 80 ? prev : prev + 10));
    }, []);

    const increaseFont = useCallback(() => {
      if (fontSize === "small") {
        setFontSize("medium");
      } else if (fontSize === "medium") {
        setFontSize("large");
      }
    }, [fontSize]);

    const decreaseFont = useCallback(() => {
      if (fontSize === "large") {
        setFontSize("medium");
      } else if (fontSize === "medium") {
        setFontSize("small");
      }
    }, [fontSize]);

    const clickOnCsvExport = useCallback(() => {
      gridRef.current!.api.exportDataAsCsv();
      setAnchorExport(null);
    }, []);

    const clickOnExcelExport = useCallback(() => {
      gridRef.current!.api.exportDataAsExcel();
      setAnchorExport(null);
    }, []);

    const clickOnPdfExport = useCallback(() => {
      const docDefinition = getDocDefinition(
        gridRef.current?.api,
        gridRef.current?.columnApi
      );
      pdfMake.createPdf(docDefinition).download();

      setAnchorExport(null);
    }, []);

    const colDefCheck = (array1: any[], array2: any[]): boolean => {
      if (!array1 || !array2 || array1.length !== array2.length) return false;
      const match = array2.every((obj2, i) => {
        const obj1 = array1[i];
        if (obj1.colId !== obj2.colId) {
          return false;
        }
        const hide1 = obj2.hide ?? false;
        const hide2 = obj1.hide ?? false;
        return hide1 === hide2;
      });
      return match;
    };

    // Save column state to localStorage
    const onColumnChanged = useCallback(() => {
      const columnState = gridRef.current?.columnApi.getColumnState();
      if (columnState) {
        localStorage.setItem("userColModel", JSON.stringify(columnState));
      }
      if (
        colDefCheck(
          gridRef?.current?.api?.getColumnDefs() as any,
          defaultColState
        )
      ) {
        setClearColumn(true);
      }
      if (
        !colDefCheck(
          gridRef?.current?.api?.getColumnDefs() as any,
          defaultColState
        )
      ) {
        setClearColumn(false);
      }
    }, [clearColumn]);

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <div>
          <Toolbar
            disableGutters
            variant="dense"
            style={{
              minHeight: "50px",
              backgroundColor: mode === "dark" ? "#181818" : "#0684bd",
              borderRadius: "5px 5px 0px 0px",
            }}
          >
            <Grid container justifyContent="space-between">
              <Grid item xs={3}>
                <Button
                  id="user-leaderboard-expand"
                  variant="text"
                  disableRipple
                  style={{
                    backgroundColor: "transparent",
                    color: "#fff",
                    textTransform: "none",
                    paddingTop: "0.3rem",
                  }}
                  onClick={() => setExpandLeaderboard(!expandLeaderboard)}
                  startIcon={
                    expandLeaderboard ? (
                      <ExpandLess style={{ color: "white" }} />
                    ) : (
                      <ExpandMore style={{ color: "white" }} />
                    )
                  }
                >
                  Leaderboard
                </Button>
                {orgId === "3154756020" && (
                  <Button
                    id="headlines-expand"
                    variant="text"
                    disableRipple
                    style={{
                      backgroundColor: "transparent",
                      color: "#fff",
                      textTransform: "none",
                      paddingTop: "0.3rem",
                    }}
                    onClick={() => setDisplayHeadlines(!displayHeadlines)}
                    startIcon={
                      displayHeadlines ? (
                        <ExpandLess style={{ color: "white" }} />
                      ) : (
                        <ExpandMore style={{ color: "white" }} />
                      )
                    }
                  >
                    Headlines
                  </Button>
                )}
              </Grid>

              {expandLeaderboard && (
                <Grid
                  container
                  item
                  xs={9}
                  alignItems="center"
                  direction="row-reverse"
                  style={{ paddingTop: "0.3rem" }}
                >
                  <Grid container item xs={12} justifyContent="flex-end">
                    {gridRef &&
                      gridRef?.current &&
                      gridRef?.current?.api &&
                      clearFilters === true && (
                        <div className="reset-button-container">
                          <button
                            style={{ position: "absolute" }}
                            type="button"
                            className="reset-button"
                            onClick={() => {
                              localStorage.removeItem("userModel");
                              if (gridRef.current?.api) {
                                const { id } =
                                  gridRef.current.api.getFilterModel();
                                gridRef.current.api.setFilterModel({
                                  model: id,
                                });
                                applySetFilter();
                              }
                              setClearFilters(true);
                            }}
                          >
                            <FilterList
                              fontSize="small"
                              style={{ color: "white" }}
                            />
                            <span className="filter-x" />
                          </button>
                        </div>
                      )}
                    {gridRef &&
                      gridRef.current &&
                      gridRef.current.api &&
                      !clearColumn && (
                        <div className="reset-button-container">
                          <button
                            style={{ position: "absolute" }}
                            type="button"
                            className="reset-button ag-side-button-icon-wrapper"
                            onClick={() => {
                              localStorage.removeItem("userColModel");
                              if (gridRef.current?.api) {
                                gridRef.current.columnApi.applyColumnState({
                                  state: defaultColState,
                                  applyOrder: true,
                                });
                              }
                              setClearColumn(true);
                            }}
                          >
                            <span
                              style={{
                                fontFamily: "agGridAlpine",
                                color: "white",
                                fontSize: "1.4em",
                                position: "relative",
                                top: 0,
                                left: 0,
                              }}
                              className="ag-theme-alpine ag-icon-columns"
                            />
                            <span className="filter-x" />
                          </button>
                        </div>
                      )}
                    {/* <RcButton onClick={() => setFullscreen('leaderboard')}>
                    <Fullscreen />
                  </RcButton> */}
                    <button
                      id="user-leaderboard-download"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        setAnchorExport(e.currentTarget)
                      }
                      aria-controls={openExport ? "basic-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={openExport ? "true" : undefined}
                      className="user-leaderboard-header-button"
                      type="button"
                    >
                      <GetApp style={{ color: "white" }} />
                    </button>
                    <button
                      className="user-leaderboard-header-button"
                      type="button"
                      onClick={larger}
                    >
                      <UnfoldMore style={{ color: "white" }} />
                    </button>
                    <button
                      className="user-leaderboard-header-button"
                      type="button"
                      onClick={smaller}
                    >
                      <UnfoldLess style={{ color: "white" }} />
                    </button>
                    <button
                      onClick={increaseFont}
                      className="user-leaderboard-header-button"
                      type="button"
                    >
                      <TextFormat
                        style={{ fontSize: "1.8rem", color: "white" }}
                      />
                    </button>
                    <button
                      onClick={decreaseFont}
                      className="user-leaderboard-header-button"
                      type="button"
                    >
                      <TextFormat fontSize="small" style={{ color: "white" }} />
                    </button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Toolbar>
          <Menu
            disableEnforceFocus
            disableAutoFocus
            style={{
              display:
                tab === "users" && navPage === "hud" ? "inherit" : "none",
            }}
            anchorEl={anchorExport}
            id="basic-menu"
            open={openExport}
            getContentAnchorEl={null}
            // transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            onClose={() => setAnchorExport(null)}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              id="user-leaderboard-download-csv"
              onClick={clickOnCsvExport}
            >
              CSV Export
            </MenuItem>
            <MenuItem
              id="user-leaderboard-download-excel"
              onClick={clickOnExcelExport}
            >
              Excel Export
            </MenuItem>
            {/* <MenuItem
              id="user-leaderboard-download-pdf"
              onClick={clickOnPdfExport}
            >
              PDF Export
            </MenuItem> */}
          </Menu>
          <Collapse in={displayHeadlines} timeout="auto">
            {orgId === "3154756020" && (
              <div
                style={
                  tab === "users" ? { display: "block" } : { display: "none" }
                }
              >
                <UserHeadlines
                  displayHeadlines={displayHeadlines}
                  sidebar={sidebar}
                  mode={mode}
                  leaderboardDeltas={leaderboardDeltas}
                  historicals={historicals}
                />
              </div>
            )}
          </Collapse>
          <Collapse in={expandLeaderboard} timeout="auto">
            <Box style={{ display: "flex", justifyContent: "center" }}>
              <div
                className={theme}
                style={{
                  height: `${verticalPercent}vh`,
                  width: "100%",
                }}
              >
                <AgGridReact
                  onColumnMoved={onColumnChanged}
                  onColumnResized={onColumnChanged}
                  onColumnVisible={onColumnChanged}
                  onGridReady={() => {
                    const safeParse = (key: string) => {
                      try {
                        const storedColValue = localStorage.getItem("userColModel")
                        if (storedColValue === null || storedColValue === undefined) {
                          const columnState = gridRef.current?.columnApi.getColumnState();
                          if (columnState) {
                            localStorage.setItem("userColModel", JSON.stringify(columnState));
                          }
                        }
                        const storedValue = localStorage.getItem(key);
                        return storedValue ? JSON.parse(storedValue) : null;
                      } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(
                          `Error parsing ${key} from localStorage`,
                          error
                        );
                        return null;
                      }
                    };

                    // Load models
                    const userModel = safeParse("userModel") || {};
                    const userColModel = safeParse("userColModel") || [];

                    // Apply models if API is ready
                    if (gridRef.current?.api && gridRef.current?.columnApi) {
                      setDefaultColState(
                        gridRef.current.columnApi.getColumnState()
                      );
                      gridRef.current.api.setFilterModel(userModel);
                      gridRef.current.columnApi.applyColumnState({
                        state: userColModel,
                        applyOrder: true,
                      });
                    }
                    setGridReady(true);

                    const filterModel = gridRef?.current?.api?.getFilterModel() || {};
                    setClearFilters(Object.keys(filterModel).length > 0);
                    if (
                      colDefCheck(
                        gridRef?.current?.api?.getColumnDefs() as any,
                        userColModel
                      )
                    ) {
                      setClearColumn(true);
                    } else {
                      setClearColumn(false);
                    }
                  }}
                  onFilterChanged={onFilterChanged}
                  pinnedBottomRowData={totalLine}
                  rowData={rowData}
                  ref={gridRef}
                  columnDefs={colDefs}
                  paginationAutoPageSize
                  pagination
                  suppressDragLeaveHidesColumns
                  containerStyle
                  getRowId={getRowId}
                  rowHeight={28}
                  getRowStyle={(params) => {
                    if (params.node.rowPinned) {
                      return { "font-weight": "bold" };
                    }
                    return { "font-weight": "400" };
                  }}
                  defaultColDef={defaultColDef}
                  sideBar={{
                    toolPanels: [
                      "filters",
                      {
                        id: "columns",
                        labelDefault: "Columns",
                        labelKey: "columns",
                        iconKey: "columns",
                        toolPanel: "agColumnsToolPanel",
                        toolPanelParams: {
                          suppressRowGroups: true,
                          suppressValues: true,
                          suppressPivots: true,
                          suppressPivotMode: true,
                          suppressColumnFilter: true,
                          suppressColumnSelectAll: true,
                          suppressColumnExpandAll: true,
                        },
                      },
                    ],
                  }}
                />
              </div>
            </Box>
          </Collapse>
        </div>
      </MuiPickersUtilsProvider>
    );
  }
);

export default NewLeaderboard;
